import React from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useGetUserConsentForEventQuery } from '@graphql/generated';
import { PartnerHotelRedirect } from '../../components/PartnerHotelRedirect';
import { useEventInfo } from '@shared/utils/eventInfo';
import { usePartnerHotelQueryParams } from '../../hooks/usePartnerHotelQueryParams';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { SearchParamsObserver } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { useTravelPlacesPricingAndBookingDetails } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails/useTravelPlacesPricingAndBookingDetails';
import { useHotelBookingRedirectOverrides } from '../../hooks/useHotelBookingRedirectOverrides';

export const HotelDetails = wrapWithHotelBookingRedirectProvider(({ joyPlaceId: joyPlaceIdArg }: { joyPlaceId: string; eventHandle: string }) => {
  const { searchParams, handleSearchParamsUpdate, redirectOverrides } = useHotelBookingRedirectOverrides();
  useHotelBookingRedirectHealth(); // Call health tracking hook to opt out of auto `loadComplete` fallback.
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const { travelPlaceId, source, queryParamEventId } = usePartnerHotelQueryParams(joyPlaceIdArg);

  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const eventId = eventInfo?.eventId || queryParamEventId;

  const { data: joyPlaceData, loading: loadingJoyPlaceData } = useTravelPlacesPricingAndBookingDetails(travelPlaceId ? [travelPlaceId] : [], 'joyPlace', false, redirectOverrides);

  const redirectMechanisms = joyPlaceData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.[0]?.redirectMechanisms;

  const { loading: isTripProfitKeyQueryLoading, data: tripProfitKeyData, error: hasTripProfitKeyQueryError } = useGetUserConsentForEventQuery({
    variables: {
      consentKey: 'websiteOptOutTripProfit',
      eventId: eventId!
    },
    skip: !eventId || source !== 'tripprofit',
    batchMode: 'fast'
  });
  // websiteOptOutTripProfit is an OPT OUT value where true means disable the trip profit links and use the original link, and null | undefined | false means we can use the trip profit links feature
  const skipTripProfitRedirect = tripProfitKeyData?.userConsentKeyForEvent?.consentAnswer === true || !!hasTripProfitKeyQueryError;

  const isLoading = loadingJoyPlaceData || loadingEventInfo || isTripProfitKeyQueryLoading;

  const joyPlace = joyPlaceData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.[0]?.joyPlace;
  const googleMaps = { gplid: joyPlace?.googlePlaceId, name: joyPlace?.name, address: joyPlace?.address, lat: joyPlace?.lat, lng: joyPlace?.long };

  useMount(() => {
    telemetry.hotelDetails.enter();
  });

  return (
    <>
      <SearchParamsObserver onValueUpdate={handleSearchParamsUpdate} />
      {isLoading ? (
        <JoyLogoLoader loaderKey="guest-hotel-booking-accommodations-place" />
      ) : (
        <PartnerHotelRedirect
          pageContext="HotelDetails"
          telemetryName="guestPartnerHotelDetailsRedirect"
          travelPlace={{
            id: travelPlaceId,
            name: joyPlace?.name,
            url: joyPlace?.url,
            type: 'joyPlace'
          }}
          searchParams={searchParams}
          redirectMechanisms={redirectMechanisms}
          googleMaps={googleMaps}
          eventData={{ eventId, eventDate }}
          forceRedirectToHotelWebsite={skipTripProfitRedirect}
        />
      )}
    </>
  );
});
